import { type NumericAdapter, NumericFault } from "@e308/core";

declare const microBrand: unique symbol;

export type Micro = bigint & { readonly [microBrand]: "kilnfront-micro" };

export interface Rational {
  readonly numerator: bigint;
  readonly denominator: bigint;
}

export const MICRO_SCALE = 1_000_000n;
const DECIMAL = /^([+-]?)(\d+)(?:\.(\d+))?(?:e([+-]?\d+))?$/i;
const ENCODED_MICRO = /^-?(?:0|[1-9]\d*)$/;

function greatestCommonDivisor(left: bigint, right: bigint): bigint {
  let a = left < 0n ? -left : left;
  let b = right < 0n ? -right : right;
  while (b !== 0n) [a, b] = [b, a % b];
  return a;
}

export function rational(numerator: bigint, denominator = 1n): Rational {
  if (denominator === 0n) throw new NumericFault("rational denominator cannot be zero");
  const sign = denominator < 0n ? -1n : 1n;
  const divisor = greatestCommonDivisor(numerator, denominator);
  return Object.freeze({
    numerator: (numerator * sign) / divisor,
    denominator: (denominator * sign) / divisor,
  });
}

export function rationalFromDecimal(encoded: string): Rational {
  if (encoded.trim() !== encoded) throw new NumericFault("invalid decimal rational");
  const match = DECIMAL.exec(encoded);
  if (!match) throw new NumericFault("invalid decimal rational");
  const fraction = match[3] ?? "";
  const exponent = Number(match[4] ?? "0");
  if (!Number.isSafeInteger(exponent) || Math.abs(exponent) > 10_000) {
    throw new NumericFault("decimal exponent is out of range");
  }
  const digits = BigInt(`${match[2]}${fraction}`);
  const signed = match[1] === "-" ? -digits : digits;
  const scalePower = fraction.length - exponent;
  return scalePower >= 0
    ? rational(signed, 10n ** BigInt(scalePower))
    : rational(signed * 10n ** BigInt(-scalePower));
}

export function addRational(left: Rational, right: Rational): Rational {
  return rational(
    left.numerator * right.denominator + right.numerator * left.denominator,
    left.denominator * right.denominator,
  );
}

export function multiplyRational(left: Rational, right: Rational): Rational {
  return rational(left.numerator * right.numerator, left.denominator * right.denominator);
}

export function divideRational(left: Rational, right: Rational): Rational {
  if (right.numerator === 0n) throw new NumericFault("cannot divide a rational by zero");
  return rational(left.numerator * right.denominator, left.denominator * right.numerator);
}

export function powerRational(base: Rational, exponent: bigint): Rational {
  if (exponent < 0n) throw new NumericFault("rational exponent must be nonnegative");
  let factor = base;
  let remaining = exponent;
  let result = rational(1n);
  while (remaining > 0n) {
    if (remaining % 2n === 1n) result = multiplyRational(result, factor);
    remaining /= 2n;
    if (remaining > 0n) factor = multiplyRational(factor, factor);
  }
  return result;
}

export function floorRational(value: Rational): bigint {
  const quotient = value.numerator / value.denominator;
  return value.numerator < 0n && value.numerator % value.denominator !== 0n
    ? quotient - 1n
    : quotient;
}

export function ceilRational(value: Rational): bigint {
  return -floorRational(rational(-value.numerator, value.denominator));
}

export function settleMicroFloor(value: Rational): Micro {
  return floorRational(rational(value.numerator * MICRO_SCALE, value.denominator)) as Micro;
}

export function microFromString(encoded: string): Micro {
  const value = rationalFromDecimal(encoded);
  const scaled = value.numerator * MICRO_SCALE;
  if (scaled % value.denominator !== 0n) {
    throw new NumericFault("quantity exceeds six decimal places");
  }
  return (scaled / value.denominator) as Micro;
}

export function formatMicro(value: Micro): string {
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / MICRO_SCALE;
  const fraction = (magnitude % MICRO_SCALE).toString().padStart(6, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

function floorDivide(numerator: bigint, denominator: bigint): bigint {
  if (denominator === 0n) throw new NumericFault("cannot divide by zero");
  return floorRational(rational(numerator, denominator));
}

function fromNumber(value: number): Micro {
  if (!Number.isFinite(value)) throw new NumericFault("micro number must be finite");
  return microFromString(String(value));
}

const adapter: NumericAdapter<Micro> = {
  id: "kilnfront-micro",
  implementationVersion: "1",
  codec: Object.freeze({
    id: "kilnfront-micro-integer",
    version: 1,
    serialize: (value: Micro) => value.toString(),
    parse: (encoded: string) => {
      if (!ENCODED_MICRO.test(encoded)) throw new NumericFault("invalid encoded micro quantity");
      return BigInt(encoded) as Micro;
    },
  }),
  fromNumber,
  fromString: microFromString,
  add: (left, right) => (left + right) as Micro,
  sub: (left, right) => (left - right) as Micro,
  mul: (left, right) => floorDivide(left * right, MICRO_SCALE) as Micro,
  div: (left, right) => floorDivide(left * MICRO_SCALE, right) as Micro,
  cmp: (left, right) => (left < right ? -1 : left > right ? 1 : 0),
  floor: (value) => (floorDivide(value, MICRO_SCALE) * MICRO_SCALE) as Micro,
  isFinite: (value) => typeof value === "bigint",
};

export const microNumbers = Object.freeze(adapter);

export function ticksForDuration(
  milliseconds: Rational,
  tickMilliseconds: bigint,
  maximumTicks = Number.MAX_SAFE_INTEGER,
): number {
  if (milliseconds.numerator < 0n) throw new NumericFault("duration cannot be negative");
  if (tickMilliseconds <= 0n) throw new NumericFault("tick duration must be positive");
  if (!Number.isSafeInteger(maximumTicks) || maximumTicks < 0) {
    throw new NumericFault("maximum ticks must be a nonnegative safe integer");
  }
  const ticks = ceilRational(
    rational(milliseconds.numerator, milliseconds.denominator * tickMilliseconds),
  );
  if (ticks > BigInt(maximumTicks)) throw new NumericFault("duration exceeds the tick bound");
  return Number(ticks);
}
